import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainNavbar from "../components/MainNavbar";
import { env } from "../config/env";
import { authStorage } from "../lib/storage/authStorage";
import { useAuth } from "../context/AuthContext";
import { isSuperAdmin } from "../utils/roles";
import { analyticsService, trackingService } from "../features/analytics/service";
import { useScrollTracking } from "../features/analytics/useTracking";
import { EnquiryModal } from "./RagChatPage";
import { contactsService } from "../features/contacts/service";
import { dealsService, isRequestAborted } from "../features/deals/service";
import { boostService } from "../features/boost/service";
import { dealCollection, dealId, type Deal, type HomeDealBanner } from "../features/deals/types";
import { derivePriceFallback } from "../features/deals/priceFallback";
import { newsService } from "../features/news/service";
import { webinarsService } from "../features/webinars/service";
import type { WebinarCardResponse } from "../features/webinars/types";
import { formatIST, formatISTMonthDayShort } from "../utils/dateUtils";
import { currencySymbol } from "../lib/currency";

const CATEGORY_ICONS: Record<string, string> = {
  DEAL: "🤝",
  PACKAGE: "🧳",
  HOTEL: "🏨",
  FLIGHT: "✈️",
  TRANSFER: "🚐",
  CRUISE: "🛳️",
  NIGHTLIFE: "🌃",
  SERVICE: "🛎️",
};

function titleCaseCategory(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function categoryFromSlug(
  slug: string | undefined,
  categories: Array<{ name: string; slug: string }>
): string {
  if (!slug) return "";
  const norm = (s: string) => s.toLowerCase().replace(/_/g, "-");
  const match = categories.find((category) => norm(category.slug) === norm(slug));
  if (match) return match.name;
  return titleCaseCategory(slug);
}

function resolveAssetUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("/uploads/")) return `${env.apiBaseUrl}${url}`;
  return url;
}

function isVideoMediaUrl(url?: string): boolean {
    if (!url) return false;
    return url.match(/\.(mp4|webm|mov|avi)(\?|$)/i) !== null || url.includes("/videos/") || url.includes("video/upload");
}

function previewImagesForDeal(deal: Deal): string[] {
    // Primary: cleaned/AI-generated image only — never fall back to the
    // seller's raw original (`media.image`/`image`), which may still contain
    // PII or simply doesn't match the polished flyer buyers expect to see.
    const primary =
        deal.media?.image_clean ||
        (deal as Record<string, unknown>).image_clean as string | undefined ||
        (deal as Record<string, unknown>).ai_generated_flyer as string | undefined ||
        "";

    // Gallery: typed media.gallery, skip video entries. Each entry may be a
    // bare string (legacy) or a { key, ... } object (buyers only ever receive
    // approved entries, but the shape is still the object form).
    const gallery: string[] = ((deal.media?.gallery ?? []) as unknown[])
        .map((g) => (typeof g === "string" ? g : (g as { key?: string })?.key))
        .filter((u): u is string => Boolean(u) && !isVideoMediaUrl(u));

    const raw = [
        ...(primary ? [primary] : []),
        ...gallery.filter((u) => u !== primary),
    ];

    return [...new Set(raw)]
        .map((url) => resolveAssetUrl(url))
        .filter((url): url is string => Boolean(url));
}

function dealVideoUrl(deal: Deal): string | undefined {
    const candidate = deal.media?.video_url || deal.video_url;
    if (!candidate) return undefined;
    if (candidate.startsWith("http://") || candidate.startsWith("https://")) {
        return candidate;
    }
    return `${env.apiBaseUrl}${candidate}`;
}

function inventoryBadgeText(deal: Deal): string | null {
  const inv = deal.inventory;
  if (!inv) return null;
  const unitLabel = inv.unit ? inv.unit.toUpperCase() : "LEFT";
  if (typeof inv.cancelled === "number" && inv.cancelled > 0) {
    const noun = inv.unit ? inv.unit.slice(0, -1) : "unit";
    return `${inv.cancelled} ${noun.toUpperCase()}${inv.cancelled === 1 ? "" : "S"} CANCELLED`;
  }
  if (typeof inv.remaining === "number") {
    return `${inv.remaining} ${unitLabel}`;
  }
  return null;
}

function useFlashCountdown(endsAt: string | null | undefined): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!endsAt) {
      setLabel(null);
      return;
    }
    const target = new Date(endsAt).getTime();
    if (Number.isNaN(target)) {
      setLabel(null);
      return;
    }
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setLabel("Ended");
        return;
      }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setLabel(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endsAt]);
  return label;
}

function FlashCountdownBadge({ deal }: { deal: Deal }) {
  const label = useFlashCountdown(deal.is_flash ? deal.flash_ends_at : null);
  if (!deal.is_flash || !label) return null;
  return (
    <div className="absolute top-2 right-2 mt-6 px-2 py-1 bg-rose-600 text-[10px] font-bold rounded uppercase shadow-sm text-white pointer-events-none">
      Ends in {label}
    </div>
  );
}

function getMediaHost(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function warmMediaCache(urls: string[]): void {
  if (typeof window === "undefined") return;
  const uniqueUrls = Array.from(new Set(urls.filter(Boolean))).slice(0, 8);
  uniqueUrls.forEach((url) => {
    if (isVideoMediaUrl(url)) {
      void fetch(url, { mode: "no-cors" }).catch(() => undefined);
      return;
    }
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  });
}

const isVideoDeal = (deal: Deal): boolean =>
    deal.item_type === "FLYER_VIDEO" ||
    Boolean(deal.media?.video_url) ||           // typed deal with video — trust the field, don't guess from URL
    isVideoMediaUrl(deal.media?.image || undefined) ||
    isVideoMediaUrl(deal.image) ||
    isVideoMediaUrl(deal.video_url);

const primaryCategoryLabel = (deal: Deal): string =>
  deal.categories?.primary?.slug || deal.category_slug || deal.category || "travel";

function CardImageCarousel({ images, title, height, onClick, isPriority = false }: {
  images: string[];
  title: string;
  height: string;
  onClick: () => void;
  isPriority?: boolean;
}) {
  const [idx, setIdx] = useState(0);
  const total = images.length;
  if (total === 0) {
    return (
      <div className={`${height} bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center cursor-pointer`} onClick={onClick}>
        <span className="text-5xl opacity-20">🖼️</span>
      </div>
    );
  }
  return (
    <div className={`${height} relative overflow-hidden bg-gray-100 cursor-pointer`} onClick={onClick}>
      <img
        src={images[idx]}
        alt={title}
        className="w-full h-full object-contain bg-black"
        loading={isPriority ? "eager" : "lazy"}
        fetchPriority={isPriority ? "high" : "auto"}
      />
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + total) % total); }}
            className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60"
          >‹</button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % total); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/40 text-white text-xs flex items-center justify-center hover:bg-black/60"
          >›</button>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, i) => (
              <span key={i} className={`w-1 h-1 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/40"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MediaVideoWithPoster({
  videoUrl,
  posterUrl,
  alt,
  containerClassName,
  mediaClassName,
  onClick,
  preload = "metadata",
  posterLoading = "lazy",
  posterFetchPriority = "auto",
}: {
  videoUrl: string;
  posterUrl?: string;
  alt: string;
  containerClassName: string;
  mediaClassName: string;
  onClick?: () => void;
  preload?: "none" | "metadata" | "auto";
  posterLoading?: "eager" | "lazy";
  posterFetchPriority?: "high" | "low" | "auto";
}) {
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const showPoster = !videoReady || videoFailed;

  const handleLoadedData = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    // At loadeddata the first frame is decoded — videoWidth is reliable.
    // If still 0 the file is unplayable (wrong codec, corrupted, etc.) → show poster.
    if (e.currentTarget.videoWidth === 0) {
      setVideoFailed(true);
    } else {
      setVideoReady(true);
    }
  };

  return (
    <div className={`${containerClassName} relative overflow-hidden bg-gray-100`} onClick={onClick}>
      {posterUrl && (
        <img
          src={posterUrl}
          alt={alt}
          className={`${mediaClassName} absolute inset-0 z-20 transition-opacity duration-300 ${showPoster ? "opacity-100" : "opacity-0"}`}
          loading={posterLoading}
          fetchPriority={posterFetchPriority}
        />
      )}
      {!videoFailed && (
        <video
          src={videoUrl}
          className={`${mediaClassName} absolute inset-0 z-10 transition-opacity duration-300 ${videoReady ? "opacity-100" : "opacity-0"}`}
          autoPlay
          muted
          loop
          playsInline
          preload={preload}
          onLoadedData={handleLoadedData}
          onError={() => setVideoFailed(true)}
        />
      )}
    </div>
  );
}

function DealCardMedia({
  deal,
  height,
  onClick,
  priorityIndex = 99,
}: {
  deal: Deal;
  height: string;
  onClick: () => void;
  priorityIndex?: number;
}) {
  if (isVideoDeal(deal) && dealVideoUrl(deal)) {
    const poster = previewImagesForDeal(deal)[0];
    const isPriorityMedia = priorityIndex < 2;
    return (
      <MediaVideoWithPoster
        videoUrl={dealVideoUrl(deal)!}
        posterUrl={poster}
        alt={deal.title || deal.item_type}
        containerClassName={`${height} cursor-pointer`}
        mediaClassName="w-full h-full object-contain bg-black"
        onClick={onClick}
        preload={isPriorityMedia ? "auto" : "metadata"}
        posterLoading={isPriorityMedia ? "eager" : "lazy"}
        posterFetchPriority={isPriorityMedia ? "high" : "auto"}
      />
    );
  }

  const isPriorityMedia = priorityIndex < 2;
  return (
    <CardImageCarousel
      images={previewImagesForDeal(deal)}
      title={deal.title || deal.item_type}
      height={height}
      onClick={onClick}
      isPriority={isPriorityMedia}
    />
  );
}

function getDealLocations(deal: Deal): { cities: string[]; countries: string[] } {
  const isPresent = (value: string | undefined): value is string => Boolean(value);
  const cities = Array.from(
    new Set(
      [
        ...(deal.destination?.cities || []),
        ...(deal.destination?.city ? [deal.destination.city] : []),
      ].filter(isPresent)
    )
  );
  const countries = Array.from(
    new Set(
      [
        ...(deal.destination?.countries || []),
        ...(deal.destination?.country ? [deal.destination.country] : []),
      ].filter(isPresent)
    )
  );

  return { cities, countries };
}

function formatDealLocation(deal: Deal): string | null {
  const { cities, countries } = getDealLocations(deal);
  if (countries.length && cities.length) {
    return `${countries.join(", ")} (${cities.join(", ")})`;
  }
  if (countries.length) {
    return countries.join(", ");
  }
  if (cities.length) {
    return cities.join(", ");
  }
  return null;
}

const DEFAULT_VERIFIED_NAME = "Gullivr Verified Seller";

/** Seller display name — falls back from the typed dealer object to legacy flat fields. */
function dealerDisplayName(deal: Deal): string {
  const dealerObj = (deal as { dealer?: { name?: string } }).dealer;
  return dealerObj?.name || deal.dealer_name || "";
}

function dealerVerification(deal: Deal): { isVerified: boolean; verifiedName: string } {
  const dealerObj = (deal as { dealer?: { is_verified?: boolean; verified_name?: string } }).dealer;
  return {
    isVerified: Boolean(dealerObj?.is_verified),
    verifiedName: dealerObj?.verified_name || DEFAULT_VERIFIED_NAME,
  };
}

function getSavedDealKey(itemType: string | undefined, itemId: string): string {
  return `${String(itemType || "DEAL").toUpperCase()}:${itemId}`;
}

// const AI_TO_PARENT: Record<string, string> = { aihotel: "hotel" };

type InventoryCategoryCard = {
  name: string;
  slug: string;
  count: number;
  sort_order?: number | null;
  subcategories?: Array<{
    name: string;
    slug: string;
    count: number;
    sort_order?: number | null;
    group?: string;
    entity_type?: "category" | "group" | "module";
    is_promoted?: boolean;
    icon?: string | null;
    image_url?: string | null;
    video_url?: string | null;
  }>;
  entity_type?: "group" | "category" | "module" | null;
  is_promoted?: boolean | null;
  original_group?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  icon?: string | null;
  group?: string | null;
};

type ZeroDealSubcategoryCard = {
  name: string;
  slug: string;
  group: string;
  icon?: string | null;
  is_promoted?: boolean;
};



function isWebinarCategoryFeed(category: string): boolean {
  const normalized = (category || "").toLowerCase().replace(/[\s_-]+/g, "");
  return normalized === "webinar" || normalized === "webinars";
}

function isNewsCategoryFeed(category: string): boolean {
  const normalized = (category || "").toLowerCase().replace(/[\s_-]+/g, "");
  return normalized === "news";
}

function isContactCategoryFeed(category: string): boolean {
  const normalized = (category || "").toLowerCase().replace(/[\s_-]+/g, "");
  return normalized === "contact" || normalized === "contacts";
}

function cardCountUnitLabel(card: { group?: string | null; name?: string | null; slug?: string | null }): string {
  const haystack = [card.group, card.name, card.slug].map((v) => String(v || "").toLowerCase().replace(/[\s_-]+/g, "")).join(" ");
  if (haystack.includes("news")) return "news";
  if (haystack.includes("webinar")) return "webinars";
  if (haystack.includes("contact")) return "contacts";
  return "deals";
}

function shouldRenderDealGroupSubcategories(groupName: string): boolean {
  const normalized = (groupName || "").toLowerCase().replace(/[\s_-]+/g, "");
  return !["news", "webinar", "webinars", "contact", "contacts"].includes(normalized);
}

function webinarToDeal(webinar: WebinarCardResponse): Deal {
  return {
    id: webinar.id,
    title: webinar.title,
    description: webinar.description || "",
    category: "Webinar",
    item_type: "AI_WEBINAR",
    image: webinar.thumbnail_url,
    video_url: webinar.video_url,
    dealer_id: webinar.host_id,
    dealer_name: webinar.host_name,
    webinar_link: webinar.webinar_link,
    webinar_date: webinar.webinar_date,
    presenter: webinar.presenter,
    likes: webinar.like_count || 0,
    is_liked: Boolean(webinar.is_liked),
    created_at: webinar.created_at || new Date().toISOString(),
  };
}

function newsToDeal(article: {
  id: string;
  title: string;
  summary?: string;
  category?: string;
  source?: string;
  source_author?: string;
  source_url?: string;
  cover_image_url?: string;
  cover_image_key?: string;
  published_at?: string;
  like_count?: number;
  is_liked?: boolean;
}): Deal {
  return {
    id: article.id,
    title: article.title,
    description: article.summary || "",
    category: "News",
    item_type: "DEAL",
    image: article.cover_image_url,
    dealer_name: article.source || article.source_author || "News",
    dealer_email: undefined,
    webinar_link: article.source_url || undefined,
    likes: article.like_count || 0,
    is_liked: Boolean(article.is_liked),
    created_at: article.published_at || new Date().toISOString(),
  };
}

function contactToDeal(contact: {
  id: string;
  contact_name?: string | null;
  designation?: string | null;
  organization_name?: string | null;
  organization_type?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  whatsapp_numbers?: string[];
  phone_numbers?: string[];
  emails?: string[];
  created_at?: string;
}): Deal {
  const city = contact.city ? [contact.city] : [];
  const state = contact.state ? [contact.state] : [];
  const country = contact.country ? [contact.country] : [];
  return {
    id: contact.id,
    title: contact.contact_name || contact.organization_name || "Contact",
    description: [contact.designation, contact.organization_name].filter(Boolean).join(" · "),
    category: "Contact",
    item_type: "AI_CONTACT",
    dealer_name: contact.organization_name || contact.organization_type || "Contact",
    dealer_whatsapp: contact.whatsapp_numbers?.[0] || contact.phone_numbers?.[0],
    dealer_phone: contact.phone_numbers?.[0],
    dealer_email: contact.emails?.[0],
    destination: {
      cities: city,
      states: state,
      countries: country,
    },
    created_at: contact.created_at || new Date().toISOString(),
    likes: 0,
    is_liked: false,
  };
}

// ─── Deal Detail Modal ────────────────────────────────────────────────────────
const DealDetailModal: React.FC<{
  deal: Deal;
  onClose: () => void;
  onLike: () => void;
  onToggleSaved: () => void;
  isSaved: boolean;
  onContactClick: (channel: "whatsapp" | "email", deal: Deal, destinationUrl: string) => void;
  onSendEnquiry: (deal: Deal) => void;
  currentUserId?: string;
}> = ({ deal, onClose, onLike, onToggleSaved, isSaved, onContactClick, onSendEnquiry, currentUserId }) => {
  const whatsappNumber = (deal.dealer_whatsapp || deal.dealer_phone || "").replace(/[^0-9]/g, "");
  const previewImages = previewImagesForDeal(deal);
  const locationText = formatDealLocation(deal);

  // Category icon map
  const typeIcon: Record<string, string> = {
    DEAL: "🤝",
    PACKAGE: "🧳",
    FLIGHT: "✈️",
    HOTEL: "🏨",
    AI_HOTEL: "🏨",
    CRUISE: "🛳️",
    TRANSFER: "🚐",
    NIGHTLIFE: "🌃",
    SERVICE: "🛎️",
    FLYER_VIDEO: "🎬",
    AI_CONTACT: "👤",
    AI_WEBINAR: "📹",
    AI_SHOWCASE: "✨",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image / placeholder */}
        <div className="relative h-64 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-2xl overflow-hidden">
          {isVideoDeal(deal) ? (
            <video
              src={dealVideoUrl(deal)}
              controls
              className="w-full h-full object-cover"
              poster={previewImages[0]}
            />
          ) : previewImages.length > 0 ? (
            <CardImageCarousel
              images={previewImages}
              title={deal.title}
              height="h-full"
              onClick={() => {}}
              isPriority
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <span className="text-6xl">{typeIcon[deal.item_type] || "🌍"}</span>
              <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">
                {deal.item_type}
              </span>
            </div>
          )}
          {/* Badge */}
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-[10px] font-bold rounded-full uppercase shadow text-indigo-700 pointer-events-none">
            {deal.item_type}
          </span>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full text-gray-600 hover:text-gray-900 hover:bg-white shadow transition-colors text-lg font-bold z-10"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Title & category */}
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-1">
              {primaryCategoryLabel(deal)}
            </p>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">{deal.title}</h2>
            {locationText && (
              <p className="mt-2 text-sm font-medium text-gray-600">{locationText}</p>
            )}
            {deal.created_at && (
              <p className="text-xs text-gray-400 mt-1">
                Listed on {formatIST(deal.created_at)}
              </p>
            )}
          </div>

          {/* Description */}
          {deal.description && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">About this deal</h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{deal.description}</p>
            </div>
          )}

          {locationText && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Locations</h3>
              <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                {locationText}
              </div>
            </div>
          )}

          {(deal.terms || deal.inventory) && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Details</h3>
              <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-700 space-y-1.5">
                {(deal.terms?.min_pax || deal.terms?.max_pax) && (
                  <p>
                    <span className="font-semibold">Group size:</span>{" "}
                    {deal.terms?.min_pax ?? "?"}–{deal.terms?.max_pax ?? "?"} pax
                  </p>
                )}
                {deal.terms?.advance_booking && (
                  <p><span className="font-semibold">Advance booking:</span> {deal.terms.advance_booking}</p>
                )}
                {deal.terms?.cancellation_policy && (
                  <p><span className="font-semibold">Cancellation policy:</span> {deal.terms.cancellation_policy}</p>
                )}
                {Array.isArray(deal.terms?.blackout_dates) && deal.terms!.blackout_dates!.length > 0 && (
                  <p><span className="font-semibold">Blackout dates:</span> {deal.terms!.blackout_dates!.join(", ")}</p>
                )}
                {typeof deal.inventory?.remaining === "number" && (
                  <p>
                    <span className="font-semibold">Availability:</span>{" "}
                    {deal.inventory.remaining} {deal.inventory.unit || "left"}
                    {typeof deal.inventory.cancelled === "number" && deal.inventory.cancelled > 0 &&
                      ` (${deal.inventory.cancelled} cancelled)`}
                  </p>
                )}
              </div>
            </div>
          )}

          {Array.isArray(deal.extras) && deal.extras.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Additional Details</h3>
              <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-700 space-y-1.5">
                {deal.extras.map((extra, idx) => (
                  <p key={`${extra.name}-${idx}`}>
                    <span className="font-semibold">{extra.name}:</span> {String(extra.value ?? "")}
                  </p>
                ))}
              </div>
            </div>
          )}

          {deal.transfer && Object.keys(deal.transfer).length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Transfer Details</h3>
              <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-700 space-y-1.5">
                {(() => {
                  const t = deal.transfer as Record<string, unknown>;
                  const vehicle = t.vehicle as Record<string, unknown> | undefined;
                  const origin = t.origin as Record<string, unknown> | undefined;
                  const destination = t.destination as Record<string, unknown> | undefined;
                  return (
                    <>
                      {t.service_type != null && (
                        <p><span className="font-semibold">Service type:</span> {String(t.service_type).replace(/_/g, " ")}</p>
                      )}
                      {vehicle?.vehicle_type != null && (
                        <p><span className="font-semibold">Vehicle:</span> {String(vehicle.vehicle_type)}{vehicle.vehicle_model ? ` — ${vehicle.vehicle_model}` : ""}</p>
                      )}
                      {vehicle?.max_passengers != null && (
                        <p><span className="font-semibold">Max passengers:</span> {String(vehicle.max_passengers)}</p>
                      )}
                      {origin?.name != null && destination?.name != null && (
                        <p><span className="font-semibold">Route:</span> {String(origin.name)} → {String(destination.name)}</p>
                      )}
                      {t.distance_km != null && (
                        <p><span className="font-semibold">Distance:</span> {String(t.distance_km)} km</p>
                      )}
                      {t.is_round_trip != null && (
                        <p><span className="font-semibold">Trip type:</span> {t.is_round_trip ? "Round trip" : "One way"}</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {deal.validity && (Array.isArray(deal.validity.dates) && deal.validity.dates.length > 0 || deal.validity.booking_deadline) && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Validity</h3>
              <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-700 space-y-1.5">
                {Array.isArray(deal.validity.dates) && deal.validity.dates.length > 0 && (
                  <p><span className="font-semibold">Travel dates:</span> {deal.validity.dates.join(", ")}</p>
                )}
                {deal.validity.booking_deadline && (
                  <p><span className="font-semibold">Booking deadline:</span> {formatIST(deal.validity.booking_deadline)}</p>
                )}
              </div>
            </div>
          )}

          {previewImages.length > 1 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Gallery ({previewImages.length})</h3>
              <div className="grid grid-cols-4 gap-2">
                {previewImages.map((imageUrl, index) => (
                  <img
                    key={imageUrl}
                    src={imageUrl}
                    alt={`${deal.title} ${index + 1}`}
                    className="w-full h-16 object-cover rounded-lg border border-gray-100"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                ))}
              </div>
            </div>
          )}

          {deal.pdf_url && (
            <div>
              <a
                href={resolveAssetUrl(deal.pdf_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={() => {
                  if (deal.id && deal.dealer_id) {
                    void fetch(`${env.apiBaseUrl}/api/analytics/deal-download`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", ...(authStorage.getToken() ? { Authorization: `Bearer ${authStorage.getToken()}` } : {}) },
                      body: JSON.stringify({ deal_id: deal.id, seller_id: deal.dealer_id, source: "deal_detail" }),
                      keepalive: true,
                    }).catch(() => undefined);
                  }
                }}
              >
                View original PDF
              </a>
            </div>
          )}

          {/* Pricing */}
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl">
            {deal.pricing?.discounted_price ? (
              <>
                <div>
                  <p className="text-xs text-blue-500 font-medium mb-0.5">Discounted Price</p>
                  <p className="text-3xl font-bold text-blue-700">{currencySymbol(deal.pricing.currency)}{(deal.pricing.discounted_price).toLocaleString("en-IN")}</p>
                </div>
                {deal.pricing?.original_price && deal.pricing.original_price > deal.pricing.discounted_price && (
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-0.5">Original</p>
                    <p className="text-lg text-gray-400 line-through">{currencySymbol(deal.pricing.currency)}{deal.pricing.original_price.toLocaleString("en-IN")}</p>
                    <p className="text-xs font-bold text-green-600">
                      Save {currencySymbol(deal.pricing.currency)}{(deal.pricing.original_price - deal.pricing.discounted_price).toLocaleString("en-IN")}
                    </p>
                  </div>
                )}
              </>
            ) : deal.pricing?.original_price ? (
              <div>
                <p className="text-xs text-blue-500 font-medium mb-0.5">Price</p>
                <p className="text-3xl font-bold text-blue-700">{currencySymbol(deal.pricing.currency)}{deal.pricing.original_price.toLocaleString("en-IN")}</p>
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-500 italic">Contact supplier for pricing</p>
            )}
          </div>
          {deal.pricing?.charge_basis != null && (
            <p className="text-xs text-gray-500 -mt-3">
              Charged {String(deal.pricing.charge_basis).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()}
            </p>
          )}

          {/* Supplier info */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
              {(dealerDisplayName(deal) || "S")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Supplier</p>
              <p className="font-semibold text-gray-800">{dealerDisplayName(deal) || "Verified Supplier"}</p>
              {dealerVerification(deal).isVerified && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold">
                  <span aria-hidden>✔️</span> {dealerVerification(deal).verifiedName}
                </span>
              )}
              {deal.dealer_email && (
                <p className="text-xs text-gray-500 truncate mt-0.5">✉️ {deal.dealer_email}</p>
              )}
              {(deal.dealer_whatsapp || deal.dealer_phone) && (
                <p className="text-xs text-gray-500 mt-0.5">📱 {deal.dealer_whatsapp || deal.dealer_phone}</p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 pt-1">
            {/* Primary CTA — news / webinar get a direct link, everything else gets Send Enquiry */}
            {deal.category === "News" && deal.webinar_link ? (
              <a
                href={deal.webinar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:scale-95 transition-all text-sm shadow-sm"
              >
                📰 Read Article
              </a>
            ) : deal.item_type === "AI_WEBINAR" && deal.webinar_link ? (
              <a
                href={deal.webinar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 active:scale-95 transition-all text-sm shadow-sm"
              >
                📹 Watch Webinar
              </a>
            ) : (() => {
              const dealerObj = (deal as { dealer?: { id?: string } }).dealer;
              const sellerId = dealerObj?.id ?? deal.dealer_id;
              const notOwn = !currentUserId || !sellerId || sellerId !== currentUserId;
              const isEnquirable = deal.item_type !== "AI_CONTACT";
              return isEnquirable && notOwn;
            })() ? (
              <button
                type="button"
                onClick={() => onSendEnquiry(deal)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 active:scale-95 transition-all text-sm shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Enquiry
              </button>
            ) : null}
            <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onToggleSaved}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-xl transition-all text-sm shadow-sm ${
                isSaved
                  ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <span>{isSaved ? "★" : "☆"}</span>
              <span>{isSaved ? "Saved for later" : "Save for later"}</span>
            </button>

            {deal.item_type === "AI_WEBINAR" && deal.webinar_link && (
              <a
                href={deal.webinar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 active:scale-95 transition-all text-sm shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Register Now
              </a>
            )}

            {whatsappNumber ? (
              <a
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onContactClick("whatsapp", deal, `https://wa.me/${whatsappNumber}`)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 active:scale-95 transition-all text-sm shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Contact on WhatsApp
              </a>
            ) : (
              <button
                disabled
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-400 font-semibold rounded-xl cursor-not-allowed text-sm"
              >
                📱 No WhatsApp Available
              </button>
            )}

            {deal.category === "News" && deal.webinar_link ? (
              <a
                href={deal.webinar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 active:scale-95 transition-all text-sm shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Read Article
              </a>
            ) : deal.dealer_email ? (
              <a
                href={`mailto:${deal.dealer_email}?subject=Enquiry: ${encodeURIComponent(deal.title)}`}
                onClick={() => onContactClick("email", deal, `mailto:${deal.dealer_email}?subject=Enquiry: ${encodeURIComponent(deal.title)}`)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 active:scale-95 transition-all text-sm shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Email
              </a>
            ) : (
              <button
                disabled
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-400 font-semibold rounded-xl cursor-not-allowed text-sm"
              >
                ✉️ No Email Available
              </button>
            )}

            <button
              type="button"
              onClick={onLike}
              className={`flex items-center justify-center gap-1.5 px-4 py-3 font-semibold rounded-xl active:scale-95 transition-all text-sm ${
                deal.is_liked
                  ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {deal.is_liked ? "❤️" : "🤍"} {deal.likes ?? 0}
            </button>
            <span className="flex items-center justify-center gap-1.5 px-4 py-3 font-semibold text-sm text-gray-500">
              🔖 {deal.save_count ?? 0}
            </span>
            </div>{/* end inner flex row */}
          </div>{/* end outer flex column */}
        </div>
      </div>
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
function getPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  if (current > 3) pages.push("…");
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push("…");
  pages.push(total);
  return pages;
}

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (p: number) => void; disabled?: boolean }> = ({
  currentPage, totalPages, onPageChange, disabled,
}) => {
  const pages = getPageNumbers(currentPage, totalPages);
  const btn = "flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-colors";
  return (
    <nav className="flex items-center gap-1 flex-wrap justify-center">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || disabled}
        className={`${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
      >‹</button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`ellipsis-${i}`} className="w-9 text-center text-gray-400 text-sm">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p as number)}
            disabled={disabled}
            className={`${btn} ${p === currentPage
              ? "bg-indigo-600 text-white border border-indigo-600"
              : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            }`}
          >{p}</button>
        )
      )}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || disabled}
        className={`${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
      >›</button>
    </nav>
  );
};

// ─── Deals Page ───────────────────────────────────────────────────────────────
const DealsPage: React.FC = () => {
  const PAGE_SIZE = 20;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get("category_slug") || searchParams.get("category") || undefined;
  const { user, isAuthenticated, logout } = useAuth();
  const isSeller = user?.role === "seller" || user?.role === "super_admin";

  useScrollTracking("/deals");

  // Track filter applied when category slug changes
  useEffect(() => {
    if (categorySlug) {
      trackingService.filterApplied({ category: categorySlug });
    }
  }, [categorySlug]);

  // Admin-curated home banners for the current category — public endpoint, no auth.
  const [homeDealBanners, setHomeDealBanners] = useState<HomeDealBanner[]>([]);
  useEffect(() => {
    if (!categorySlug) {
      setHomeDealBanners([]);
      return;
    }
    const controller = new AbortController();
    dealsService
      .getHomeDealBanners(categorySlug ? { category_slug: categorySlug } : undefined, { signal: controller.signal })
      .then(setHomeDealBanners)
      .catch(() => setHomeDealBanners([]));
    return () => controller.abort();
  }, [categorySlug]);

  const [activeTab, setActiveTab] = useState<"all" | "saved">("all");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [allSavedDeals, setAllSavedDeals] = useState<Deal[]>([]);
  const [inventoryCategories, setInventoryCategories] = useState<{
    categories: InventoryCategoryCard[];
    promoted_categories: InventoryCategoryCard[];
    total_categories: number;
    total_inventory: number;
    total_deal_count: number;
    total_webinar_count: number;
    total_news_count: number;
  }>({ categories: [], promoted_categories: [], total_categories: 0, total_inventory: 0, total_deal_count: 0, total_webinar_count: 0, total_news_count: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [uploadingFeedCard, setUploadingFeedCard] = useState(false);
  const [uploadingSubcategoryCard, setUploadingSubcategoryCard] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [openingBannerId, setOpeningBannerId] = useState<string | null>(null);
  const boostSessionId = useRef(crypto.randomUUID());
  const openDeal = (deal: Deal) => {
    setSelectedDeal(deal);
    const sid = (deal as any).dealer?.id ?? deal.dealer_id ?? "";
    if (sid) trackingService.profileView(sid, "seller");
    if (deal.is_boosted && deal.boost_campaign_id) {
      void boostService.recordEvent({
        event_id: crypto.randomUUID(),
        campaign_id: deal.boost_campaign_id,
        deal_id: dealId(deal),
        event_type: "click",
        surface: "browse",
        position: 1,
        session_id: boostSessionId.current,
      }).catch(() => {});
      trackingService.dealView(dealId(deal), sid, { campaignId: deal.boost_campaign_id });
    }
  };

  // Open a specific deal from a notification deep link (?deal_id=...) — resolve its
  // typed collection first since notification metadata only ever carries the raw id.
  const notifiedDealId = searchParams.get("deal_id");
  useEffect(() => {
    if (!notifiedDealId) return;
    let cancelled = false;
    (async () => {
      try {
        const resolved = await dealsService.resolveCollection(notifiedDealId);
        if (!resolved || cancelled) return;
        const deal = await dealsService.getDeal(resolved.collection, resolved.typedId);
        if (!cancelled) openDeal(deal);
      } catch {
        // deal may have been removed/deactivated since the notification was sent — no-op
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifiedDealId]);

  const firedImpressions = useRef<Set<string>>(new Set());
  const [enquiryDeal, setEnquiryDeal] = useState<Deal | null>(null);
  const [zeroDealSubcategoryCards] = useState<ZeroDealSubcategoryCard[]>([]);
  const [inventoryPage, setInventoryPage] = useState(0);
  const [hasMoreInventory, setHasMoreInventory] = useState(true);
  const [totalDealsCount] = useState(0);
  const [promotedStartIndex, setPromotedStartIndex] = useState(0);
  const isDevelopmentMode = isSuperAdmin(user?.role);
  const savedDealKeys = useMemo(
    () => new Set(allSavedDeals.map((deal) => getSavedDealKey(deal.item_type, dealId(deal)))),
    [allSavedDeals],
  );
  const selectedCategory = useMemo(() => {
    // Flatten subcategories so the display name resolves correctly
    const allSubcats = inventoryCategories.categories.flatMap((g) => g.subcategories ?? []);
    return categoryFromSlug(categorySlug, [
      ...inventoryCategories.categories,
      ...inventoryCategories.promoted_categories,
      ...allSubcats,
    ]);
  }, [categorySlug, inventoryCategories.categories, inventoryCategories.promoted_categories]);
  const isCategoryFeed = Boolean(categorySlug);
  const selectedFeedCard = useMemo(() => {
    if (!categorySlug) return null;
    const merged = [
      ...inventoryCategories.categories,
      ...inventoryCategories.promoted_categories,
    ];
    // Normalize slug: treat _ and - as equivalent for matching
    const normalize = (s: string) => s.toLowerCase().replace(/_/g, "-");
    const normalized = normalize(categorySlug);
    // Check top-level (groups + promoted)
    const top = merged.find((c) => normalize(c.slug) === normalized);
    if (top) return top;
    // Check subcategories nested inside groups
    for (const group of inventoryCategories.categories) {
      const sub = (group.subcategories ?? []).find((s) => normalize(s.slug) === normalized);
      if (sub) return { ...sub, entity_type: "category" as const };
    }
    return null;
  }, [categorySlug, inventoryCategories.categories, inventoryCategories.promoted_categories]);

  const loadSavedFeed = async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!isAuthenticated) {
        setAllSavedDeals([]);
        return;
      }
      const saved = await dealsService.listSavedDeals(0, 200, signal ? { signal } : undefined);
      setAllSavedDeals(saved);
    } catch (err: unknown) {
      if (isRequestAborted(err)) return;
      console.error("Failed to fetch saved deals:", err);
      setError("Unable to load saved deals right now.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMarketplace = async (page = 0, signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    const req = signal ? { signal } : undefined;
    try {
      const selectedGroup = String(selectedFeedCard?.group || selectedFeedCard?.name || selectedCategory || "");
      const selectedCategorySlug = String(selectedFeedCard?.slug || categorySlug || "");

      // No category selected — only fetch the category summary, no deals needed.
      if (!selectedCategorySlug) {
        if (page === 0) {
          const categories = await dealsService.getInventoryCategories(req);
          setInventoryCategories(categories);
        }
        setDeals([]);
        setHasMoreInventory(false);
        return;
      }

      // Check group, name, and slug so detection works regardless of which field the DB populates.
      const cardName = String(selectedFeedCard?.name || "");
      const cardGroup = String(selectedFeedCard?.group || "");
      const isWebinarFeed =
        isWebinarCategoryFeed(selectedGroup) ||
        isWebinarCategoryFeed(cardName) ||
        isWebinarCategoryFeed(cardGroup) ||
        isWebinarCategoryFeed(selectedCategorySlug);
      const isNewsFeed =
        isNewsCategoryFeed(selectedGroup) ||
        isNewsCategoryFeed(cardName) ||
        isNewsCategoryFeed(cardGroup) ||
        isNewsCategoryFeed(selectedCategorySlug);
      const isContactFeed =
        isContactCategoryFeed(selectedGroup) ||
        isContactCategoryFeed(cardName) ||
        isContactCategoryFeed(cardGroup) ||
        isContactCategoryFeed(selectedCategorySlug);
      const isSubCategory = selectedFeedCard?.entity_type === "category";
      const subCategoryName = isSubCategory ? String(selectedFeedCard?.name || "") : "";
      const inventory = isWebinarFeed
        ? (await webinarsService.listWebinarCards(
            page * PAGE_SIZE,
            PAGE_SIZE,
            subCategoryName || undefined,
          )).items.map(webinarToDeal)
        : isNewsFeed
          ? (await newsService.listNews(
              page * PAGE_SIZE,
              PAGE_SIZE,
              subCategoryName || undefined,
            )).items.map(newsToDeal)
          : isContactFeed
            ? (await contactsService.listContacts(
                page * PAGE_SIZE,
                PAGE_SIZE,
                subCategoryName || undefined,
              )).map(contactToDeal)
            : await dealsService.listInventory(
                selectedCategorySlug,
                page * PAGE_SIZE,
                PAGE_SIZE,
                { ...req, personalized: isAuthenticated },
              );
      const categories =
        page === 0 ? await dealsService.getInventoryCategories(req) : null;
      setDeals(inventory);
      setInventoryPage(page);
      setHasMoreInventory(inventory.length >= PAGE_SIZE);
      if (categories) {
        setInventoryCategories(categories);
      }
    } catch (err: unknown) {
      if (isRequestAborted(err)) return;
      console.error("Failed to fetch inventory:", err);
      setError("Unable to load inventory right now.");
    } finally {
      setIsLoading(false);
    }
  };

  /** After uploads / layout changes — matches current tab and route. */
  const refreshDealsPageData = async () => {
    if (activeTab === "saved") {
      await loadSavedFeed();
      return;
    }
    await loadMarketplace(0);
  };

  // Reset Shorts view when category changes or tab switches
  useEffect(() => {
    setViewMode("grid");
    setShortsIndex(0);
  }, [categorySlug, activeTab]);

  // Pre-fetch saved deals count on mount / auth change so the tab badge is correct immediately.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ac = new AbortController();
    void loadSavedFeed(ac.signal);
    return () => ac.abort();
  }, [isAuthenticated]);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    void (async () => {
      if (activeTab === "saved") {
        await loadSavedFeed(signal);
        return;
      }
      await loadMarketplace(0, signal);
    })();
    return () => ac.abort();
  }, [selectedCategory, isAuthenticated, isCategoryFeed, activeTab]);
  const handleLike = async (
    targetDealId: string,
    collection: string,
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    const fromList = deals.find((d) => dealId(d) === targetDealId);
    const base = fromList ?? (selectedDeal && dealId(selectedDeal) === targetDealId ? selectedDeal : undefined);
    if (!base) return;

    const prevLiked = Boolean(base.is_liked);
    const prevLikes = base.likes ?? 0;
    const optLiked = !prevLiked;
    const optLikes = optLiked ? prevLikes + 1 : Math.max(0, prevLikes - 1);

    setDeals((prev) =>
      prev.some((d) => dealId(d) === targetDealId)
        ? prev.map((d) =>
            dealId(d) === targetDealId ? { ...d, is_liked: optLiked, likes: optLikes } : d
          )
        : prev
    );
    setSelectedDeal((p) =>
      p && dealId(p) === targetDealId ? { ...p, is_liked: optLiked, likes: optLikes } : p
    );

    try {
      const campaignId = (base as any).boost_campaign_id ?? undefined;
      const data = await dealsService.likeDeal(targetDealId, collection, campaignId);
      setDeals((prev) =>
        prev.some((d) => dealId(d) === targetDealId)
          ? prev.map((d) =>
              dealId(d) === targetDealId ? { ...d, is_liked: data.liked, likes: data.likes } : d
            )
          : prev
      );
      setSelectedDeal((p) =>
        p && dealId(p) === targetDealId ? { ...p, is_liked: data.liked, likes: data.likes } : p
      );
    } catch (err: unknown) {
      console.error("Failed to toggle like:", err);
      setDeals((prev) =>
        prev.some((d) => dealId(d) === targetDealId)
          ? prev.map((d) =>
              dealId(d) === targetDealId ? { ...d, is_liked: prevLiked, likes: prevLikes } : d
            )
          : prev
      );
      setSelectedDeal((p) =>
        p && dealId(p) === targetDealId ? { ...p, is_liked: prevLiked, likes: prevLikes } : p
      );
    }
  };

  const handleToggleSaved = async (deal: Deal, e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/" } });
      return;
    }

    const id = dealId(deal);
    const itemKey = getSavedDealKey(deal.item_type, id);
    const isCurrentlySaved = savedDealKeys.has(itemKey);

    // Typed deals nest dealer as {id, name}; legacy shadow deals use flat dealer_id
    const dealerObj = (deal as { dealer?: { id?: string; name?: string } }).dealer;
    const resolvedSellerId = dealerObj?.id ?? deal.dealer_id ?? "";
    const resolvedSellerName = dealerObj?.name ?? deal.dealer_name ?? "";

    const boostCid = (deal as any).boost_campaign_id ?? undefined;
    try {
      if (isCurrentlySaved) {
        await dealsService.unsaveDeal(deal.item_type, id);
        if (boostCid) trackingService.ingest("unsave_for_later", id, resolvedSellerId, { source: "deals_page", campaignId: boostCid });
        void analyticsService.trackUnsaveForLater({
          source: "deals_page",
          item_id: id,
          item_type: deal.item_type,
          item_title: deal.title,
          seller_id: resolvedSellerId,
          seller_name: resolvedSellerName,
          seller_whatsapp: deal.dealer_whatsapp || deal.dealer_phone,
          seller_email: deal.dealer_email,
          metadata: {
            active_tab: activeTab,
            selected_category: selectedCategory || "all",
          },
        });
      } else {
        await dealsService.saveDeal(deal.item_type, id);
        if (boostCid) trackingService.ingest("save_for_later", id, resolvedSellerId, { source: "deals_page", campaignId: boostCid });
        void analyticsService.trackSaveForLater({
          source: "deals_page",
          item_id: id,
          item_type: deal.item_type,
          item_title: deal.title,
          seller_id: resolvedSellerId,
          seller_name: resolvedSellerName,
          seller_whatsapp: deal.dealer_whatsapp || deal.dealer_phone,
          seller_email: deal.dealer_email,
          metadata: {
            active_tab: activeTab,
            selected_category: selectedCategory || "all",
          },
        });
      }
      setAllSavedDeals(await dealsService.listSavedDeals());
    } catch (err: unknown) {
      console.error("Failed to update saved deal:", err);
    }
  };

  const handleContactClick = (channel: "whatsapp" | "email", deal: Deal, destinationUrl: string) => {
    const dealerObj = (deal as { dealer?: { id?: string; name?: string } }).dealer;
    const sellerId = dealerObj?.id ?? deal.dealer_id ?? "";
    const boostCid = (deal as any).boost_campaign_id ?? undefined;
    if (boostCid) trackingService.ingest("contact_click", dealId(deal), sellerId, { channel, source: "deals_page", campaignId: boostCid });
    void analyticsService.trackContactClick({
      channel,
      source: "deals_page",
      item_id: dealId(deal),
      item_type: deal.item_type,
      item_title: deal.title,
      seller_id: sellerId || undefined,
      seller_name: dealerObj?.name ?? deal.dealer_name,
      seller_whatsapp: deal.dealer_whatsapp || deal.dealer_phone,
      seller_email: deal.dealer_email,
      destination_url: destinationUrl,
      metadata: {
        active_tab: activeTab,
        selected_category: selectedCategory || "all",
      },
    });
  };

  const displayedDeals = useMemo(() => {
    if (activeTab === "saved") {
      // On a category page, still show all saved deals (don't filter by category).
      // The saved tab is a global "bookmarks" view regardless of which category is open.
      return allSavedDeals;
    }
    return deals;
  }, [activeTab, allSavedDeals, deals]);

  // A "category"-type banner (home deal banner or promotional banner) can
  // navigate here with ?view=shorts to jump straight into the Shorts viewer
  // for the category feed instead of landing on the grid. This useEffect call
  // is declared after the "reset view on category change" effect above, so
  // within the same commit it runs second and wins (its setViewMode("shorts")
  // isn't immediately stomped back to "grid"). Waits for deals to load, then
  // strips the param so it doesn't keep re-forcing Shorts afterward.
  useEffect(() => {
    if (searchParams.get("view") !== "shorts") return;
    if (!displayedDeals.length) return;
    setViewMode("shorts");
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, displayedDeals.length]);

  const categoryCards = useMemo(() => {
    if (activeTab === "saved") return [];
    return [...inventoryCategories.categories].map((category) => {
      const sortedSubcategories = [...(category.subcategories || [])].sort(
        (a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999),
      );
      return {
        ...category,
        subcategories: sortedSubcategories,
        icon: category.icon || CATEGORY_ICONS[category.name.toUpperCase()] || "🌍",
        heroImage: category.video_url || category.image_url,
      };
    }).sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  }, [activeTab, inventoryCategories.categories]);

  const promotedCategoryCards = useMemo(() => {
    if (activeTab === "saved") return [];
    return inventoryCategories.promoted_categories.map((category) => ({
      ...category,
      icon: category.icon || CATEGORY_ICONS[category.name.toUpperCase()] || "📌",
      heroImage: category.video_url || category.image_url,
    }));
  }, [activeTab, inventoryCategories.promoted_categories]);

  const mixedCategoryCards = useMemo(() => {
    if (activeTab === "saved") return [];
    const EXEMPT_ZERO_COUNT_GROUPS = new Set(["news", "webinar", "contact"]);
    const rows: Array<
      (InventoryCategoryCard & { heroImage?: string | null }) & {
        card_kind: "subcategory";
        parent_group?: string;
      }
    > = [];
    for (const groupCard of categoryCards) {
      if (shouldRenderDealGroupSubcategories(groupCard.name) && (groupCard.subcategories?.length || 0) > 0) {
        for (const sub of groupCard.subcategories || []) {
          rows.push({
            name: sub.name,
            slug: sub.slug,
            count: sub.count,
            group: sub.group || groupCard.name,
            entity_type: "category",
            is_promoted: sub.is_promoted ?? false,
            icon: sub.icon || "🧩",
            image_url: sub.image_url ?? null,
            video_url: sub.video_url ?? null,
            heroImage: sub.video_url || sub.image_url,
            card_kind: "subcategory",
            parent_group: groupCard.name,
          });
        }
      }
    }
    // Promoted categories (news, webinars, contact, etc.) come only from `promoted_categories`
    // and render in the Promoted strip — do not duplicate them into this grid.
    const existingSlugs = new Set(rows.map((row) => String(row.slug || "").trim().toLowerCase()));
    for (const zeroCard of zeroDealSubcategoryCards) {
      const normalizedSlug = String(zeroCard.slug || "").trim().toLowerCase();
      if (!normalizedSlug || existingSlugs.has(normalizedSlug)) continue;
      rows.push({
        name: zeroCard.name,
        slug: normalizedSlug,
        count: 0,
        group: zeroCard.group,
        entity_type: "category",
        is_promoted: zeroCard.is_promoted ?? false,
        icon: zeroCard.icon || "🧩",
        image_url: null,
        video_url: null,
        heroImage: null,
        card_kind: "subcategory",
        parent_group: zeroCard.group,
      });
      existingSlugs.add(normalizedSlug);
    }
    return rows.filter((row) => {
      if (isDevelopmentMode) return true;
      const groupName = String(row.parent_group || row.group || row.name || "").trim().toLowerCase();
      if (EXEMPT_ZERO_COUNT_GROUPS.has(groupName)) return true;
      return Number(row.count || 0) > 0;
    });
  }, [activeTab, categoryCards, zeroDealSubcategoryCards, isDevelopmentMode]);

  const visiblePromotedCards = useMemo(
    () => promotedCategoryCards.slice(promotedStartIndex, promotedStartIndex + 4),
    [promotedCategoryCards, promotedStartIndex],
  );

  // Pagination: total pages derived from the selected feed card's count
  const totalPages = useMemo(() => {
    const total = selectedFeedCard?.count ?? totalDealsCount;
    if (!total || total <= PAGE_SIZE) return 1;
    return Math.ceil(total / PAGE_SIZE);
  }, [selectedFeedCard, totalDealsCount]);

  const currentPage = inventoryPage + 1; // 1-based for display

  const warmupMediaUrls = useMemo(() => {
    const prioritized = [
      ...visiblePromotedCards.slice(0, 2).flatMap((card) => [card.heroImage || ""]),
      ...displayedDeals.slice(0, 4).flatMap((deal) => [dealVideoUrl(deal) || "", previewImagesForDeal(deal)[0] || ""]),
    ];
    return prioritized.filter(Boolean);
  }, [visiblePromotedCards, displayedDeals]);

  useEffect(() => {
    if (warmupMediaUrls.length === 0) return;
    warmMediaCache(warmupMediaUrls);
  }, [warmupMediaUrls]);

  useEffect(() => {
    const hosts = Array.from(
      new Set(
        warmupMediaUrls
          .map((url) => getMediaHost(url))
          .filter((origin): origin is string => Boolean(origin)),
      ),
    ).slice(0, 2);

    const createdLinks: HTMLLinkElement[] = [];
    hosts.forEach((origin) => {
      if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
      createdLinks.push(link);
    });

    return () => {
      createdLinks.forEach((link) => link.remove());
    };
  }, [warmupMediaUrls]);

  useEffect(() => {
    if (promotedStartIndex > 0 && promotedStartIndex >= promotedCategoryCards.length) {
      setPromotedStartIndex(Math.max(promotedCategoryCards.length - 3, 0));
    }
  }, [promotedCategoryCards.length, promotedStartIndex]);

  const handleSubcategoryMediaUpload = async (
    groupSlug: string,
    subcategorySlug: string,
    subcategoryName: string,
    file: File,
  ) => {
    const uploadKey = `${groupSlug}:${subcategorySlug}`;
    setUploadingSubcategoryCard(uploadKey);
    setUploadMessage(null);
    try {
      await dealsService.uploadCategoryMedia(subcategorySlug, file);
      setUploadMessage(`Uploaded media for ${subcategoryName}.`);
      await refreshDealsPageData();
    } catch (err: unknown) {
      console.error("Failed to upload subcategory media:", err);
      setUploadMessage(`Upload failed for ${subcategoryName}.`);
    } finally {
      setUploadingSubcategoryCard(null);
    }
  };

  const handleOpenedFeedMediaUpload = async (file: File) => {
    if (!selectedFeedCard) return;
    setUploadingFeedCard(true);
    setUploadMessage(null);
    try {
      if (selectedFeedCard.entity_type === "category") {
        await dealsService.uploadCategoryMedia(selectedFeedCard.slug, file);
      } else {
        await dealsService.uploadCategoryGroupMedia(
          selectedFeedCard.name || selectedCategory || "",
          file,
        );
      }
      setUploadMessage(`Uploaded media for ${selectedFeedCard.name || selectedCategory || "feed"}.`);
      await refreshDealsPageData();
    } catch (err: unknown) {
      console.error("Failed to upload media for opened feed:", err);
      setUploadMessage("Upload failed for this feed.");
    } finally {
      setUploadingFeedCard(false);
    }
  };

  // ── YouTube Shorts mode ─────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<"grid" | "shorts">("grid");
  const [shortsIndex, setShortsIndex] = useState(0);
  const shortsContainerRef = useRef<HTMLDivElement>(null);
  const SHORTS_H = 600;
  // Flag to ignore onScroll events triggered by our own programmatic scroll
  const isScrollingProgrammatically = useRef(false);
  // Shorts mode: only when in category feed, on "all" tab, deals loaded, and user chose shorts view
  const isShortsMode = isCategoryFeed && activeTab === "all" && displayedDeals.length > 0 && viewMode === "shorts";

  // Scroll to a specific index — used by buttons and keyboard
  const scrollToIndex = useCallback((idx: number) => {
    const el = shortsContainerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(idx, displayedDeals.length - 1));
    setShortsIndex(clamped);
    isScrollingProgrammatically.current = true;
    el.scrollTo({ top: clamped * SHORTS_H, behavior: "smooth" });
    // Clear the flag after animation completes (~500ms)
    setTimeout(() => { isScrollingProgrammatically.current = false; }, 600);
  }, [displayedDeals.length, SHORTS_H]);

  // Sync scroll position → active index (user swiped manually)
  const handleShortsScroll = useCallback(() => {
    if (isScrollingProgrammatically.current) return; // ignore our own scrolls
    const el = shortsContainerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / SHORTS_H);
    setShortsIndex(idx);
  }, [displayedDeals, SHORTS_H]);

  // Keyboard navigation
  useEffect(() => {
    if (!isShortsMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") scrollToIndex(shortsIndex + 1);
      else if (e.key === "ArrowUp") scrollToIndex(shortsIndex - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isShortsMode, shortsIndex, scrollToIndex]);

  // Load more when near end
  useEffect(() => {
    if (!isShortsMode) return;
    if (shortsIndex >= displayedDeals.length - 3 && hasMoreInventory && !isLoading) {
      void loadMarketplace(inventoryPage + 1);
    }
  }, [shortsIndex, displayedDeals.length, hasMoreInventory, isLoading, isShortsMode, inventoryPage]);

  // Fire impression events for newly rendered boosted deals (deduplicated per session)
  useEffect(() => {
    const boosted = displayedDeals.filter(d => d.is_boosted && d.boost_campaign_id);
    boosted.forEach((deal, idx) => {
      const did = dealId(deal);
      if (firedImpressions.current.has(did)) return;
      firedImpressions.current.add(did);
      void boostService.recordEvent({
        event_id: crypto.randomUUID(),
        campaign_id: deal.boost_campaign_id!,
        deal_id: did,
        event_type: "impression",
        surface: "browse",
        position: idx + 1,
        session_id: boostSessionId.current,
      }).catch(() => {});
    });
  }, [displayedDeals]);

  // _shortsCats kept for future use (category chips inside Shorts)
  // const _shortsCats = [...inventoryCategories.promoted_categories, ...inventoryCategories.categories];
  // ── Shorts: contained YouTube-style player (not full screen) ──────────────
  const ShortsOverlay = isShortsMode ? (
    <div className="w-full flex gap-4">
      {/* Left: nav arrow */}
      <button
        onClick={() => scrollToIndex(shortsIndex - 1)}
        disabled={shortsIndex === 0}
        className="hidden md:flex items-center justify-center self-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-20 transition shrink-0"
      >
        ▲
      </button>

      {/* Center: vertical snap scroller — one card visible at a time */}
      <div className="flex-1 min-w-0">
        <div
          ref={shortsContainerRef}
          onScroll={handleShortsScroll}
          className="overflow-y-scroll snap-y snap-mandatory no-scrollbar rounded-2xl"
          style={{ height: SHORTS_H, scrollSnapType: "y mandatory" }}
        >
          {displayedDeals.map((deal, idx) => {
            const vid = dealVideoUrl(deal);
            const img = previewImagesForDeal(deal)[0] || "";
            const isDealSaved = savedDealKeys.has(getSavedDealKey(deal.item_type, dealId(deal)));
            const price = deal.pricing?.discounted_price || deal.pricing?.original_price || deal.discounted_price || deal.original_price;
            const dest = deal.destination?.countries?.[0] || deal.destination?.cities?.[0] || "";
            return (
              <div
                key={`${deal.item_type}-${dealId(deal)}`}
                className="relative w-full shrink-0 overflow-hidden rounded-2xl bg-black"
                style={{ height: SHORTS_H, scrollSnapAlign: "start" }}
              >
                {/* Media */}
                {vid ? (
                  <video src={vid}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay={idx === shortsIndex} muted loop playsInline
                    preload={Math.abs(idx - shortsIndex) <= 1 ? "auto" : "none"} />
                ) : img ? (
                  <img src={img} alt={deal.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading={Math.abs(idx - shortsIndex) <= 1 ? "eager" : "lazy"} />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 flex items-center justify-center">
                    <span className="text-6xl opacity-30">{CATEGORY_ICONS[deal.item_type] || "🌍"}</span>
                  </div>
                )}

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/10" />

                {/* Bottom info */}
                <div className="absolute bottom-0 left-0 right-14 p-4 z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xs">
                      {(dealerDisplayName(deal) || "?")[0]}
                    </div>
                    <span className="text-white font-semibold text-xs flex items-center gap-1">
                      {dealerDisplayName(deal) || "Seller"}
                      {dealerVerification(deal).isVerified && <span aria-hidden title={dealerVerification(deal).verifiedName}>✔️</span>}
                    </span>
                    {dest && <span className="text-white/60 text-xs">· {dest}</span>}
                  </div>
                  <h3 className="text-white font-bold text-sm leading-tight mb-1 cursor-pointer line-clamp-2"
                    onClick={() => openDeal(deal)}>{deal.title}</h3>
                  <p className="text-white/70 text-xs line-clamp-1">{deal.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {typeof price === "number" && (
                      <span className="text-white font-bold text-sm">
                        {currencySymbol(deal.pricing?.currency)}{price.toLocaleString("en-IN")}
                        <span className="text-white/60 text-xs font-normal"> /person</span>
                      </span>
                    )}
                    <button onClick={() => setEnquiryDeal(deal)}
                      className="ml-auto bg-white text-black font-bold text-xs px-4 py-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition-all">
                      Enquire
                    </button>
                  </div>
                </div>

                {/* Right action bar */}
                <div className="absolute right-2 bottom-16 flex flex-col items-center gap-4 z-10">
                  <button onClick={() => void handleLike(dealId(deal), dealCollection(deal))}
                    className="flex flex-col items-center gap-0.5 text-white">
                    <span className="text-2xl">{deal.is_liked ? "❤️" : "🤍"}</span>
                    <span className="text-[10px] font-semibold">{deal.likes ?? 0}</span>
                  </button>
                  <div className="flex flex-col items-center gap-0.5 text-white">
                    <span className="text-2xl">🔖</span>
                    <span className="text-[10px] font-semibold">{deal.save_count ?? 0}</span>
                  </div>
                  <button onClick={(e) => void handleToggleSaved(deal, e)}
                    className="flex flex-col items-center gap-0.5 text-white">
                    <span className="text-2xl">{isDealSaved ? "★" : "☆"}</span>
                    <span className="text-[10px] font-semibold">{isDealSaved ? "Saved" : "Save"}</span>
                  </button>
                  <button onClick={() => setEnquiryDeal(deal)}
                    className="flex flex-col items-center gap-0.5 text-white">
                    <span className="text-2xl">💬</span>
                    <span className="text-[10px] font-semibold">Enquire</span>
                  </button>
                </div>
              </div>
            );
          })}
          {isLoading && (
            <div className="flex items-center justify-center" style={{ height: SHORTS_H }}>
              <div className="text-gray-400 text-sm">Loading more…</div>
            </div>
          )}
        </div>

        {/* Swipe hint / counter */}
        <p className="mt-2 text-center text-xs text-gray-500">
          {shortsIndex + 1} / {displayedDeals.length}
          <span className="ml-2 text-gray-400">· Scroll or use arrows</span>
        </p>
      </div>

      {/* Right: nav arrow */}
      <button
        onClick={() => scrollToIndex(shortsIndex + 1)}
        disabled={shortsIndex >= displayedDeals.length - 1}
        className="hidden md:flex items-center justify-center self-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-20 transition shrink-0"
      >
        ▼
      </button>

      {/* Enquiry modal */}
      {enquiryDeal && (
        <EnquiryModal
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          deal={enquiryDeal as any}
          sessionId=""
          onClose={() => setEnquiryDeal(null)}
          onSubmitted={() => setEnquiryDeal(null)}
        />
      )}
    </div>
  ) : null;

  return (
    <div className="page-shell">
      <MainNavbar
        isAuthenticated={isAuthenticated}
        isSeller={isSeller}
        onLogout={logout}
      />

      <main className="page-container">
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={activeTab === "all" ? "tab-pill-active" : "tab-pill"}
          >
            All deals
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("saved")}
            className={activeTab === "saved" ? "tab-pill-active" : "tab-pill"}
          >
            Saved ({allSavedDeals.length})
          </button>
        </div>

        {activeTab === "all" && isCategoryFeed && (
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500">Category Feed</p>
              <h2 className="text-xl font-bold text-gray-900">{selectedCategory} Deals</h2>
            </div>
            <div className="flex items-center gap-2">
              {isDevelopmentMode && categorySlug && (
                <>
                  <label
                    className={`px-3 py-2 border border-indigo-300 text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-semibold ${
                      uploadingFeedCard ? "opacity-60 cursor-wait" : "cursor-pointer"
                    }`}
                  >
                    <span>{uploadingFeedCard ? "Uploading..." : "Upload media"}</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      className="hidden"
                      disabled={uploadingFeedCard}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.currentTarget.value = "";
                        if (file) {
                          void handleOpenedFeedMediaUpload(file);
                        }
                      }}
                    />
                  </label>
                </>
              )}
              {/* View toggle — grid vs Shorts */}
              {displayedDeals.length > 0 && (
                <button
                  onClick={() => setViewMode(viewMode === "shorts" ? "grid" : "shorts")}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                  title={viewMode === "shorts" ? "Switch to grid view" : "Switch to Shorts view"}
                >
                  {viewMode === "shorts" ? (
                    <><span>▦</span><span className="hidden sm:inline">Grid</span></>
                  ) : (
                    <><span>▶</span><span className="hidden sm:inline">Shorts</span></>
                  )}
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}
        {activeTab === "all" && promotedCategoryCards.length > 0 && (
          <section className="mb-6 -mx-4 sm:mx-0 px-4 sm:px-5 py-4 bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-1">
              {promotedCategoryCards.map((cat) => (
                <button
                  key={`chip-${cat.entity_type || "group"}-${cat.slug}`}
                  type="button"
                  onClick={() => navigate(`/?category_slug=${encodeURIComponent(cat.slug)}`)}
                  className="flex flex-col items-center gap-1.5 shrink-0 w-16 text-center"
                >
                  <span className="relative w-14 h-14 rounded-full p-[2px] bg-gradient-to-br from-pink-500 via-orange-400 to-amber-300">
                    <span className="flex items-center justify-center w-full h-full rounded-full bg-white overflow-hidden">
                      {cat.image_url ? (
                        <img
                          src={cat.image_url}
                          alt={cat.name}
                          className="w-full h-full object-cover rounded-full"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-2xl">{cat.icon}</span>
                      )}
                    </span>
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-white border border-gray-300 text-[9px] font-semibold text-gray-700 whitespace-nowrap shadow-sm">
                      {cat.count}
                    </span>
                  </span>
                  <span className="text-[11px] text-gray-700 leading-tight truncate w-full">{cat.name}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => navigate("/")}
                className="flex flex-col items-center gap-1.5 shrink-0 w-16 text-center"
              >
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 border border-gray-200 text-gray-600 text-lg">
                  ⊞
                </span>
                <span className="text-[11px] text-gray-700 leading-tight">View all</span>
              </button>
            </div>
          </section>
        )}

        {activeTab === "all" && homeDealBanners.length > 0 && (
          <div className="mb-6 flex gap-4 overflow-x-auto pb-1">
            {homeDealBanners.map((banner) => (
              <div
                key={banner.id}
                className={`relative shrink-0 w-72 h-36 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-100 to-blue-100 shadow-sm cursor-pointer ${openingBannerId === banner.id ? "opacity-70" : ""}`}
                onClick={async () => {
                  if (openingBannerId) return;
                  if (banner.banner_type === "direct_link") {
                    if (!banner.direct_link) return;
                    if (/^https?:\/\//i.test(banner.direct_link)) {
                      window.open(banner.direct_link, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(banner.direct_link);
                    }
                    return;
                  }
                  if (banner.banner_type === "category") {
                    // Already showing this category's feed — just jump into Shorts.
                    setViewMode("shorts");
                    return;
                  }
                  // Partners don't have individual deals — route to their profile page.
                  if (banner.seller_role === "partner") {
                    if (!banner.seller_id) return;
                    navigate(`/partners/${banner.seller_id}`);
                    return;
                  }
                  // banner_type "seller_deal" (or missing, for pre-migration rows)
                  if (!banner.deal_id || !banner.deal_collection) return;
                  const target = deals.find((d) => dealId(d) === banner.deal_id);
                  if (target) {
                    openDeal(target);
                    return;
                  }
                  setOpeningBannerId(banner.id);
                  try {
                    const fetched = await dealsService.getDeal(banner.deal_collection, banner.deal_id);
                    openDeal(fetched);
                  } catch (err: unknown) {
                    console.error("Failed to open banner deal:", err);
                  } finally {
                    setOpeningBannerId(null);
                  }
                }}
              >
                {banner.image_url && (
                  <img
                    src={resolveAssetUrl(banner.image_url)}
                    alt={banner.title || "Banner"}
                    className="w-full h-full object-cover"
                  />
                )}
                {banner.is_deal_of_day && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-amber-400 text-[10px] font-bold rounded uppercase shadow-sm text-white">
                    Deal of the Day
                  </span>
                )}
                {banner.title && (
                  <div className="absolute bottom-0 inset-x-0 px-3 py-2 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-white text-sm font-bold truncate">{banner.title}</p>
                  </div>
                )}
                {banner.fields
                  .filter((f) => f.value && f.position !== "custom")
                  .map((f) => (
                    <span
                      key={f.key}
                      className={`absolute px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[10px] font-bold rounded-full shadow ${
                        f.position === "left_tag" ? "top-2 left-2" :
                        f.position === "right_upper_tag" ? "top-2 right-2" :
                        "bottom-10 left-2"
                      }`}
                    >
                      {f.value}
                    </span>
                  ))}
              </div>
            ))}
          </div>
        )}
        {activeTab === "all" && selectedFeedCard?.subcategories && selectedFeedCard.subcategories.length > 0 && (
          <section className="mb-6">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3">
              Subcategories in {selectedCategory}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[...selectedFeedCard.subcategories]
                .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999))
                .map((sub) => {
                  const heroImage = sub.video_url || sub.image_url;
                  return (
                    <div
                      key={sub.slug}
                      onClick={() => navigate(`/?category_slug=${encodeURIComponent(sub.slug)}`)}
                      className="text-left bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="h-32 bg-gradient-to-br from-indigo-100 to-blue-100 relative">
                        {heroImage ? (
                          isVideoMediaUrl(heroImage) ? (
                            <MediaVideoWithPoster
                              videoUrl={heroImage}
                              alt={sub.name}
                              containerClassName="w-full h-full"
                              mediaClassName="w-full h-full object-cover"
                              preload="metadata"
                            />
                          ) : (
                            <img src={heroImage} alt={sub.name} className="w-full h-full object-cover" loading="lazy" />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">{sub.icon || "🧩"}</div>
                        )}
                        <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] bg-white/90 text-gray-700 font-semibold">
                          {sub.count} {cardCountUnitLabel(sub)}
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-semibold text-gray-900 truncate">{sub.name}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
        )}

        {ShortsOverlay}

        {activeTab === "all" && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
            <span>{inventoryCategories.total_categories} categories</span>
            <span>{inventoryCategories.total_deal_count} deals</span>
            {inventoryCategories.total_webinar_count > 0 && (
              <span>{inventoryCategories.total_webinar_count} webinars</span>
            )}
            {inventoryCategories.total_news_count > 0 && (
              <span>{inventoryCategories.total_news_count} news</span>
            )}
            {isCategoryFeed && selectedFeedCard && (
              <span className="text-gray-400">
                |&nbsp;<span className="text-gray-700 font-medium">{selectedCategory}:</span>{" "}
                {selectedFeedCard.count} {cardCountUnitLabel(selectedFeedCard)}
                {selectedFeedCard.subcategories && selectedFeedCard.subcategories.length > 0 && (
                  <> across {selectedFeedCard.subcategories.length} subcategories</>
                )}
              </span>
            )}
          </div>
        )}

        {uploadMessage ? <div className="alert-info mb-4">{uploadMessage}</div> : null}

        {error ? <div className="alert-error mb-4">{error}</div> : null}

        {isLoading ? (
          <div className="py-16 text-center text-gray-600">Loading deals...</div>
        ) : activeTab === "all" && !isCategoryFeed ? (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {mixedCategoryCards.map((cat, index) => (
                <div key={`${cat.card_kind}:${cat.parent_group || cat.group || "root"}:${cat.slug}`} className="space-y-2">
                  <div
                    onClick={() => navigate(`/?category_slug=${encodeURIComponent(cat.slug)}`)}
                    className="text-left bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
                  >
                  <div className="h-40 bg-gradient-to-br from-indigo-100 to-blue-100 relative">
                    {(() => {
                      const ownMediaUrl = cat.video_url || cat.image_url;
                      if (!ownMediaUrl) {
                        return <div className="w-full h-full flex items-center justify-center text-5xl">{cat.icon}</div>;
                      }
                      return isVideoMediaUrl(ownMediaUrl) ? (
                        <MediaVideoWithPoster
                          videoUrl={ownMediaUrl}
                          posterUrl={cat.image_url || undefined}
                          alt={cat.name}
                          containerClassName="w-full h-full"
                          mediaClassName="w-full h-full object-cover"
                          preload={index < 2 ? "auto" : "metadata"}
                        />
                      ) : (
                        <img
                          src={ownMediaUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          loading={index < 2 ? "eager" : "lazy"}
                          fetchPriority={index < 2 ? "high" : "auto"}
                        />
                      );
                    })()}
                    <div className="absolute z-20 top-2 right-2 px-2 py-1 rounded-full text-[10px] bg-white/90 text-gray-700 font-semibold">
                      {cat.count} {cardCountUnitLabel(cat)}
                    </div>
                    <div className="absolute z-20 bottom-2 right-2 px-2 py-1 rounded-full text-[10px] bg-white/90 text-rose-600 font-semibold">
                      {cat.is_promoted ? "❤️" : "🤍"}
                    </div>
                    {isDevelopmentMode && (
                      <label
                        className={`absolute z-20 top-2 left-2 px-2 py-1 rounded-full text-[10px] font-semibold bg-white/90 text-indigo-700 shadow ${
                          uploadingSubcategoryCard === `${cat.parent_group || cat.group || "group"}:${cat.slug}`
                            ? "opacity-60 cursor-wait"
                            : "cursor-pointer"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>
                          {uploadingSubcategoryCard === `${cat.parent_group || cat.group || "group"}:${cat.slug}`
                            ? "Uploading..."
                            : "Upload media"}
                        </span>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          className="hidden"
                          disabled={uploadingSubcategoryCard === `${cat.parent_group || cat.group || "group"}:${cat.slug}`}
                          onChange={(e) => {
                            e.stopPropagation();
                            const file = e.target.files?.[0];
                            e.currentTarget.value = "";
                            if (file) {
                              void handleSubcategoryMediaUpload(
                                cat.parent_group || cat.group || "group",
                                cat.slug,
                                cat.name,
                                file,
                              );
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-sm uppercase tracking-wider text-blue-600 font-semibold">{cat.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {`Tap to open ${cat.name.toLowerCase()} deals`}
                    </p>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : displayedDeals.length === 0 ? (
          <div className="py-16 text-center text-gray-600 bg-white rounded-xl border border-gray-200">
            {activeTab === "saved" ? "No saved deals yet." : "No deals available."}
          </div>
        ) : activeTab === "all" && isCategoryFeed ? (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedDeals.map((deal, index) => {
              const locationText = formatDealLocation(deal);
              const isDealSaved = savedDealKeys.has(getSavedDealKey(deal.item_type, dealId(deal)));
              const primaryPreviewImage = previewImagesForDeal(deal)[0];
              return (
                <article
                  key={`${deal.item_type}-${dealId(deal)}`}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="h-60 bg-gradient-to-br from-blue-50 to-indigo-100 relative">
                    {isVideoDeal(deal) ? (
                      <MediaVideoWithPoster
                        videoUrl={dealVideoUrl(deal)!}
                        posterUrl={primaryPreviewImage}
                        alt={deal.title || deal.item_type}
                        containerClassName="w-full h-full"
                        mediaClassName="w-full h-full object-contain bg-black"
                        preload={index < 2 ? "auto" : "metadata"}
                        posterLoading={index < 2 ? "eager" : "lazy"}
                        posterFetchPriority={index < 2 ? "high" : "auto"}
                      />
                    ) : primaryPreviewImage ? (
                      <img
                        src={primaryPreviewImage}
                        alt={deal.title}
                        className="w-full h-full object-contain bg-black"
                        loading={index < 2 ? "eager" : "lazy"}
                        fetchPriority={index < 2 ? "high" : "auto"}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <span className="text-6xl font-bold">{deal.item_type[0]}</span>
                      </div>
                    )}
                    <div className="absolute top-3 right-3 px-2 py-1 bg-white/90 text-[10px] font-bold rounded uppercase text-indigo-700">
                      {deal.item_type}
                    </div>
                    {deal.is_boosted && (
                      <div className="absolute top-3 left-3 px-2 py-1 bg-amber-400 text-[10px] font-bold rounded uppercase shadow-sm text-white pointer-events-none">
                        Featured
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">{primaryCategoryLabel(deal)}</p>
                    <h3 className="text-lg font-bold text-gray-900 mt-1 line-clamp-1" onClick={() => openDeal(deal)}>{deal.title}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2" onClick={() => openDeal(deal)}>{deal.description}</p>
                    {locationText && <p className="mt-2 text-xs font-medium text-gray-600">{locationText}</p>}
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                      <button
                        onClick={(e) => void handleToggleSaved(deal, e)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                          isDealSaved
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <span>{isDealSaved ? "★" : "☆"}</span>
                        <span>{isDealSaved ? "Saved" : "Save"}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => void handleLike(dealId(deal), dealCollection(deal), e)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                          deal.is_liked
                            ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {deal.is_liked ? "❤️" : "🤍"} {deal.likes ?? 0}
                      </button>
                      <span className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500">
                        🔖 {deal.save_count ?? 0}
                      </span>
                      {deal.category === "News" && deal.webinar_link ? (
                        <a
                          href={deal.webinar_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          📰 Read Article
                        </a>
                      ) : deal.item_type === "AI_WEBINAR" && deal.webinar_link ? (
                        <a
                          href={deal.webinar_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          📹 Watch Webinar
                        </a>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center py-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => {
                  void loadMarketplace(p - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={isLoading}
              />
            </div>
          )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedDeals.map((deal, index) => {
              const locationText = formatDealLocation(deal);
              const isDealSaved = savedDealKeys.has(getSavedDealKey(deal.item_type, dealId(deal)));
              return (
              <article
                key={`${deal.item_type}-${dealId(deal)}`}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                {/* Image */}
                <div className="relative">
                  <DealCardMedia
                    deal={deal}
                    height="h-44"
                    onClick={() => openDeal(deal)}
                    priorityIndex={index}
                  />
                  <div className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur-sm text-[10px] font-bold rounded uppercase shadow-sm text-indigo-700 pointer-events-none">
                    {deal.item_type}
                  </div>
                  {deal.is_boosted && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-amber-400 text-[10px] font-bold rounded uppercase shadow-sm text-white pointer-events-none">
                      Featured
                    </div>
                  )}
                  <FlashCountdownBadge deal={deal} />
                  {inventoryBadgeText(deal) && (
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[9px] font-bold rounded-full uppercase shadow text-rose-700 pointer-events-none">
                      {inventoryBadgeText(deal)}
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/30 backdrop-blur-sm text-[9px] text-white rounded-full pointer-events-none">
                    Tap to view details
                  </div>
                </div>

                {/* Card body */}
                <div className="p-4" onClick={() => openDeal(deal)}>
                  <div className="flex justify-between items-start">
                    <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">{primaryCategoryLabel(deal)}</p>
                    <span className="text-[10px] text-gray-400">{formatISTMonthDayShort(deal.created_at || "")}</span>
                  </div>
                  <h2 className="text-base font-bold text-gray-900 mt-1 line-clamp-1">{deal.title}</h2>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{deal.description}</p>
                  {locationText && (
                    <p className="mt-2 text-xs font-medium text-gray-600 line-clamp-2">{locationText}</p>
                  )}
                  {Array.isArray(deal.rail_tags) && deal.rail_tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {deal.rail_tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[9px] font-bold uppercase rounded-full"
                        >
                          {tag.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Price — hidden for news/webinar */}
                  {deal.category !== "News" && deal.item_type !== "AI_WEBINAR" && (
                    <div className="mt-3 flex items-center gap-2">
                      {deal.pricing?.discounted_price ? (
                        <span className="text-lg font-bold text-blue-700">{currencySymbol(deal.pricing.currency)}{deal.pricing.discounted_price.toLocaleString("en-IN")}</span>
                      ) : (
                        /* No flat headline price — fall back to the per-vertical
                           price grids / base fields, then a price-looking
                           `extras` entry, before giving up on "Contact for pricing". */
                        (() => {
                          const fb = derivePriceFallback(deal.pricing, deal.extras);
                          if (!fb) return <span className="text-xs font-medium text-gray-400 italic">Contact for pricing</span>;
                          if (fb.kind === "text") {
                            return (
                              <span className="text-xs font-medium text-gray-500 line-clamp-1" title={`${fb.label}: ${fb.text}`}>
                                ℹ <span className="font-semibold">{fb.label}:</span> {fb.text}
                              </span>
                            );
                          }
                          return (
                            <>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{fb.label}</span>
                              <span className="text-lg font-bold text-blue-700">{currencySymbol(deal.pricing?.currency)}{fb.amount.toLocaleString("en-IN")}</span>
                            </>
                          );
                        })()
                      )}
                      {deal.pricing?.original_price && deal.pricing?.discounted_price && deal.pricing.original_price > deal.pricing.discounted_price ? (
                        <span className="text-xs text-gray-400 line-through">{currencySymbol(deal.pricing.currency)}{deal.pricing.original_price.toLocaleString("en-IN")}</span>
                      ) : null}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => void handleToggleSaved(deal, e)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                          isDealSaved
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <span>{isDealSaved ? "★" : "☆"}</span>
                        <span>{isDealSaved ? "Saved" : "Save"}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => void handleLike(dealId(deal), dealCollection(deal), e)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                          deal.is_liked
                            ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {deal.is_liked ? "❤️" : "🤍"} {deal.likes ?? 0}
                      </button>
                      <span className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500">
                        🔖 {deal.save_count ?? 0}
                      </span>
                      {deal.category !== "News" && deal.item_type !== "AI_WEBINAR" && (deal.dealer_whatsapp || deal.dealer_phone) && (
                        <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                          WhatsApp
                        </span>
                      )}
                    </div>
                    {deal.category === "News" && deal.webinar_link ? (
                      <a
                        href={deal.webinar_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        📰 Read Article
                      </a>
                    ) : deal.item_type === "AI_WEBINAR" && deal.webinar_link ? (
                      <a
                        href={deal.webinar_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        📹 Watch Webinar
                      </a>
                    ) : (
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400 uppercase tracking-tighter">Supplier</p>
                        <p className="text-xs font-semibold text-gray-700 max-w-[100px] truncate flex items-center gap-1 justify-end">
                          {dealerVerification(deal).isVerified && <span aria-hidden title={dealerVerification(deal).verifiedName}>✔️</span>}
                          {dealerDisplayName(deal) || "Verified Supplier"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onLike={() =>
            void handleLike(dealId(selectedDeal), dealCollection(selectedDeal))
          }
          onToggleSaved={() => void handleToggleSaved(selectedDeal)}
          isSaved={savedDealKeys.has(getSavedDealKey(selectedDeal.item_type, dealId(selectedDeal)))}
          onContactClick={handleContactClick}
          onSendEnquiry={(deal) => {
            setSelectedDeal(null);      // close detail modal
            setEnquiryDeal(deal);       // open enquiry modal
          }}
          currentUserId={user?.id}
        />
      )}

      {/* Enquiry Modal */}
      {enquiryDeal && (
        <EnquiryModal
          deal={enquiryDeal as Parameters<typeof EnquiryModal>[0]["deal"]}
          sessionId=""
          ownsDeal={Boolean(user?.id && (
            ((enquiryDeal as { dealer?: { id?: string } }).dealer?.id ?? enquiryDeal.dealer_id) === user.id
          ))}
          onClose={() => setEnquiryDeal(null)}
          onSubmitted={(leadId) => {
            setEnquiryDeal(null);
            // Track in analytics
            void analyticsService.trackEvent({
              event_type: "cross_sell_enquiry_sent",
              source: "deals_feed",
              item_id: dealId(enquiryDeal),
              item_type: enquiryDeal.item_type ?? "deal",
              item_title: enquiryDeal.title,
              seller_id: enquiryDeal.dealer_id ?? "",
              metadata: { lead_id: leadId },
            });
          }}
        />
      )}
    </div>
  );
};

export default DealsPage;
